import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Delimiter } from 'constants/Delimiter';
import { useDebounce } from 'hooks';

import messages from './messages';

interface IProps {
  values?: string[];
  onValuesChange?: (values: string[]) => void;
}

const DEFAULT_DELIMITER = Delimiter.SPACE;

const DelimitedInput = ({
  values = [],
  onValuesChange = () => null,
}: IProps) => {
  const [delimiterSelected, setDelimiterSelected] =
    useState<string>(DEFAULT_DELIMITER);
  const [customDelimiter, setCustomDelimiter] = useState<string>('');
  const { formatMessage } = useIntl();

  const handleChange = useCallback((e: SelectChangeEvent<string>) => {
    setDelimiterSelected(e.target.value);
  }, []);

  const options = useMemo(
    () => [
      { label: formatMessage(messages.spaceOption), value: Delimiter.SPACE },
      { label: formatMessage(messages.commaOption), value: Delimiter.COMMA },
      {
        label: formatMessage(messages.semiColonOption),
        value: Delimiter.SEMI_COLON,
      },
      {
        label: formatMessage(messages.newLineOption),
        value: Delimiter.NEW_LINE,
      },
      { label: formatMessage(messages.otherOption), value: Delimiter.OTHER },
    ],
    [formatMessage]
  );

  const isCustomDelimiter = useMemo(
    () => delimiterSelected === Delimiter.OTHER,
    [delimiterSelected]
  );

  const delimiter = useMemo(() => {
    switch (delimiterSelected) {
      case Delimiter.SPACE:
        return ' ';
      case Delimiter.COMMA:
        return ',';
      case Delimiter.SEMI_COLON:
        return ';';
      case Delimiter.NEW_LINE:
        return '\n';
      case Delimiter.OTHER:
        return customDelimiter;
      default:
        return ' ';
    }
  }, [customDelimiter, delimiterSelected]);

  const [rawText, setRawText] = useState<string>(values.join(delimiter));
  const debouncedRawText = useDebounce(rawText, 1000);

  useEffect(() => {
    onValuesChange(debouncedRawText.split(delimiter).filter((item) => item));
  }, [debouncedRawText, delimiter, onValuesChange]);

  return (
    <Box>
      <FormControl sx={{ width: '100%', margin: '8px 0px' }}>
        <InputLabel id='value-delimiter'>
          <FormattedMessage {...messages.delimiterLabel} />
        </InputLabel>
        <Select
          labelId='value-delimiter'
          value={delimiterSelected}
          label={formatMessage(messages.delimiterLabel)}
          onChange={handleChange}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          <FormattedMessage {...messages.delimiterHelperText} />
        </FormHelperText>
      </FormControl>
      {isCustomDelimiter ? (
        <FormControl sx={{ width: '100%', margin: '8px 0px' }}>
          <TextField
            label={formatMessage(messages.customDelimiterLabel)}
            value={customDelimiter}
            onChange={(e) => setCustomDelimiter(e.target.value || '')}
          />
          <FormHelperText>
            <FormattedMessage {...messages.customDelimiterHelper} />
          </FormHelperText>
        </FormControl>
      ) : null}
      <FormControl sx={{ width: '100%', margin: '8px 0px' }}>
        <TextField
          label={formatMessage(messages.rawTextLabel)}
          multiline
          maxRows={15}
          value={rawText}
          onChange={(e) => setRawText(e.target.value || '')}
        />
        <FormHelperText>
          <FormattedMessage {...messages.rawTextHelper} />
        </FormHelperText>
      </FormControl>
    </Box>
  );
};

export default DelimitedInput;
